import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FinanceProvider } from './contexts/FinanceContext';
import SummaryCards from './components/SummaryCards';
import BasicInfoForm from './components/BasicInfoForm';
import PaymentForm from './components/PaymentForm';
import PaymentsList from './components/PaymentsList';
import Utilities from './components/Utilities';
import ForecastModal from './components/ForecastModal';
import Login from './components/Login';

import { Wallet, BarChart3, LogOut, User, Menu, X } from 'lucide-react';

function AppContent() {
  const { user, logout } = useAuth();
  const [isForecastOpen, setIsForecastOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (!user) {
    return <Login />;
  }

  return (
    <FinanceProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Header - Mobile Optimized */}
        <header className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-lg sticky top-0 z-40 safe-area-top">
          <div className="max-w-7xl mx-auto px-4 py-4 md:py-6 lg:px-8">
            <div className="flex items-center justify-between">
              {/* Logo & Title */}
              <div className="flex items-center gap-2 md:gap-3">
                <Wallet className="w-8 h-8 md:w-10 md:h-10" />
                <div>
                  <h1 className="text-lg md:text-2xl lg:text-3xl font-bold leading-tight">
                    Gestor de Finanzas
                  </h1>
                  <p className="hidden md:block text-sm text-blue-100">
                    Controla tus ingresos y gastos
                  </p>
                </div>
              </div>
              
              {/* Desktop User Menu */}
              <div className="hidden md:flex items-center gap-4">
                <div className="flex items-center gap-2 bg-white bg-opacity-20 rounded-lg px-3 py-2">
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium max-w-[150px] truncate">
                    {user.displayName || user.email}
                  </span>
                </div>
                <button
                  onClick={logout}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg px-3 py-2 flex items-center gap-2 transition-all duration-200"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Cerrar sesión</span>
                </button>
              </div>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
            
            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-4 pt-4 border-t border-white border-opacity-20 animate-fade-in">
                <div className="flex items-center gap-3 mb-3">
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium truncate">
                    {user.displayName || user.email}
                  </span>
                </div>
                <button
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg px-4 py-3 flex items-center justify-center gap-2 transition-all duration-200"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">Cerrar sesión</span>
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 lg:px-8 py-4 md:py-8 safe-area-bottom">
          {/* Summary Cards */}
          <SummaryCards onForecastClick={() => setIsForecastOpen(true)} />
          
          {/* Configuration Section */}
          <BasicInfoForm />
          
          {/* Payments List */}
          <PaymentsList />
          
          {/* Add Payment Form */}
          <PaymentForm />
          
          {/* Utilities */}
          <Utilities />
        </main>

        {/* Forecast Modal */}
        <ForecastModal 
          isOpen={isForecastOpen} 
          onClose={() => setIsForecastOpen(false)} 
        />

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-6 md:py-8">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <BarChart3 className="w-5 h-5 md:w-6 md:h-6 text-blue-400" />
              <span className="text-base md:text-lg font-semibold">Gestor de Finanzas</span>
            </div>
            <p className="text-gray-400 text-sm">
              React + Firebase + Tailwind CSS
            </p>
            <p className="text-gray-500 text-xs mt-1">
              © 2024 - Todos los derechos reservados
            </p>
          </div>
        </footer>
      </div>
    </FinanceProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;